import { isEmpty } from 'lodash';
import { nanoid } from 'nanoid';
import { proxy } from 'valtio';
import { getMe as apiGetMe } from '~/api/user2';
import authStore from '~/authStore';
import { Prefs } from '~/common/prefs';
import type { User } from '~/common/types';
import type { FilterProperties, TTableFilter } from './common';
import FilterGroup from './Filters/FilterGroup';
import FilterRule from './Filters/FilterRule';

class FilterStore {
  filterTree: FilterGroup = new FilterGroup({ id: 'root', condition: 'or' });
  filtersById: { [filterId: string]: TTableFilter } = {};
  me: User | null = null;

  async getMe() {
    // Cookie mode has no token; getMe falls back to the HttpOnly cookie when it's empty.
    const me = await apiGetMe(authStore.token ?? '');
    this.me = me;
  }

  addFilter(filter: TTableFilter, parentId: string) {
    const parent = this.getFilterParent(parentId, this.filterTree);
    if (parent) {
      parent.addChild(filter);
    }

    this.buildFlatFilters(this.filterTree);
  }

  removeFilter(filterId: string, parentId: string) {
    const parent = this.getFilterParent(parentId, this.filterTree);
    if (parent) {
      parent.removeChild(filterId);
    }

    this.buildFlatFilters(this.filterTree);
  }

  getFilterParent(parentId: string, group: FilterGroup): FilterGroup | undefined {
    let parent: FilterGroup | undefined;

    if (parentId === group.id) {
      parent = group;
    } else {
      for (let i = 0; i < group.children.length; i++) {
        const child = group.children[i];
        if (child.type === 'group') {
          const found = this.getFilterParent(parentId, child);
          if (found) {
            parent = found;
            break;
          }
        }
      }
    }

    return parent;
  }

  buildFlatFilters(group: FilterGroup) {
    for (let i = 0; i < group.children.length; i++) {
      const child = group.children[i];
      this.filtersById[child.id] = child;
      if (child.type === 'group') {
        this.buildFlatFilters(child);
      }
    }
  }

  buildFilterTreeFromProps(children: FilterProperties[] = [], parentId: string) {
    for (let i = 0; i < children.length; i++) {
      const f = children[i];
      if (f.type === 'group') {
        this.addFilter(new FilterGroup({ id: f.id, condition: f.condition }), parentId);

        this.buildFilterTreeFromProps(f.children, f.id || nanoid());
      } else if (f.type === 'rule') {
        this.addFilter(
          new FilterRule({
            id: f.id,
            columnId: f.columnId,
            operator: f.operator,
            value: f.value,
          }),
          parentId,
        );
      }
    }
  }

  persistFilters(tableId: string) {
    if (!tableId) return;
    sessionStorage.setItem(`${Prefs.ADVANCED_FILTER_STATE}.${tableId}`, JSON.stringify(this.filterTree.children));
  }

  getFiltersAsSearchParams(group: FilterGroup): { [key: string]: any } {
    const groupCondition = group.condition;
    const cases: any[] = [];

    group.children.forEach((child) => {
      if (child.type === 'group') {
        const childGroup = this.getFiltersAsSearchParams(child);
        cases.push(childGroup);
      } else {
        cases.push(child.getSearchParamValue());
      }
    });

    if (!isEmpty(cases)) {
      return [
        {
          filterType: groupCondition,
          filters: cases,
        },
      ];
    }

    return [];
  }

  reset() {
    this.filterTree.children = [];
    this.filtersById = {};
    this.me = null;
  }
}

const store = proxy(new FilterStore());
export default store;
